import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Plus, Send, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TYPES = [
  "equipment_failure",
  "property_damage",
  "theft",
  "customer_incident",
  "near_miss",
  "hazard",
  "other",
];
const SEVERITIES = ["minor", "moderate", "serious", "critical"];

const EMPTY = {
  incident_date: "", incident_time: "", site: "", location: "",
  incident_type: "", severity: "", description: "",
  people_involved: "", witnesses: "",
  actions_taken: "", follow_up_required: "",
  estimated_cost_nzd: "", police_notified: false, insurance_claim: false,
  manager_signature: "",
};

function coerce(form) {
  const payload = { ...form };
  payload.estimated_cost_nzd =
    payload.estimated_cost_nzd === "" || payload.estimated_cost_nzd == null
      ? null
      : Number(payload.estimated_cost_nzd);
  return payload;
}

export default function ManagerSiteIncidents() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/manager/site-incidents");
      setRows(data.incidents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/manager/site-incidents", { form: coerce(EMPTY) });
      setActive(data); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const openExisting = (row) => {
    setActive(row);
    const f = { ...EMPTY, ...(row.form || {}) };
    f.estimated_cost_nzd = f.estimated_cost_nzd ?? "";
    setForm(f);
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/manager/site-incidents/${active.id}`, { form: coerce(form) });
      setActive(data); toast.success("Saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/manager/site-incidents/${active.id}`, { form: coerce(form) });
      const { data } = await api.post(`/manager/site-incidents/${active.id}/submit`);
      setActive(data); toast.success("Submitted to admin / superadmin"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Delete this draft permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/manager/site-incidents/${active.id}`);
      toast.success("Draft deleted");
      setActive(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const locked = active && active.status !== "DRAFT";

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <h1 className="text-xl font-bold text-[#1A2744]">Site incident reports</h1>
      </div>
      <p className="text-xs text-gray-600">
        Use this form to log site-level incidents (equipment failure, property damage,
        theft, customer/safety incidents). Submitted reports are sent to Admin / SuperAdmin.
      </p>

      {!active && (
        <>
          <Button onClick={startNew} disabled={busy} className="bg-[#1A2744] text-white" data-testid="site-incident-new">
            <Plus className="h-4 w-4 mr-1" /> New site incident
          </Button>
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Type</TableHead>
                <TableHead>Site</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No site incidents yet</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{r.form?.incident_date || r.created_at?.slice(0,10)}</TableCell>
                    <TableCell className="text-xs">{r.form?.incident_type || "—"}</TableCell>
                    <TableCell className="text-xs">{r.site || "—"}</TableCell>
                    <TableCell className="text-xs"><span className="px-2 py-0.5 rounded bg-gray-100 font-mono text-[10px]">{r.status}</span></TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => openExisting(r)} data-testid={`site-incident-open-${r.id}`}>Open</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}

      {active && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-bold text-[#1A2744]">Site incident · <span className="font-mono text-xs text-gray-500">{active.status}</span></h2>
            <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="site-incident-back">Back to list</Button>
          </div>
          {active.admin_notes && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs">
              <strong>Admin notes:</strong> {active.admin_notes}
            </div>
          )}

          {[
            ["incident_date", "Date *", "date"],
            ["incident_time", "Time", "time"],
            ["site", "Site (label or code)", "input"],
            ["location", "Specific location", "input"],
            ["incident_type", "Type *", "select-type"],
            ["severity", "Severity", "select-sev"],
            ["description", "Description *", "textarea"],
            ["people_involved", "People involved", "textarea"],
            ["witnesses", "Witnesses", "textarea"],
            ["actions_taken", "Immediate actions taken", "textarea"],
            ["follow_up_required", "Follow-up required", "textarea"],
            ["estimated_cost_nzd", "Estimated cost (NZD)", "number"],
            ["manager_signature", "Manager name (signature)", "input"],
          ].map(([k, label, kind]) => (
            <div key={k}>
              <Label>{label}</Label>
              {kind === "textarea" && <Textarea rows={3} value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`sf-${k}`} />}
              {kind === "select-type" && (
                <select className="w-full border rounded h-9 px-2 text-sm" value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`sf-${k}`}>
                  <option value="">— select —</option>
                  {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              )}
              {kind === "select-sev" && (
                <select className="w-full border rounded h-9 px-2 text-sm" value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`sf-${k}`}>
                  <option value="">— select —</option>
                  {SEVERITIES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              {(kind === "input" || kind === "date" || kind === "time" || kind === "number") && (
                <Input type={kind === "input" ? "text" : kind} value={form[k] ?? ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`sf-${k}`} />
              )}
            </div>
          ))}

          <div className="flex flex-col gap-1 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.police_notified} disabled={locked} onChange={(e) => upd("police_notified", e.target.checked)} />
              Police notified
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.insurance_claim} disabled={locked} onChange={(e) => upd("insurance_claim", e.target.checked)} />
              Insurance claim required
            </label>
          </div>

          {!locked && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" onClick={save} disabled={busy} data-testid="site-incident-save"><Save className="h-3.5 w-3.5 mr-1" /> Save draft</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white" data-testid="site-incident-submit"><Send className="h-3.5 w-3.5 mr-1" /> Submit to admin</Button>
              <Button variant="outline" onClick={remove} disabled={busy} className="text-red-600 border-red-300" data-testid="site-incident-delete"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete draft</Button>
            </div>
          )}
          {locked && <p className="text-xs text-gray-500 italic">This report is {active.status.toLowerCase()} — read-only.</p>}
        </CardContent></Card>
      )}
    </div>
  );
}
